/**
 * Pet-owner "reschedule my booking" modal. Mirrors the booking slot picker so
 * the customer can only move to a genuinely open slot for the same service.
 * Backed by POST /user/bookings/:id/reschedule (server re-validates the slot).
 */
import { useState } from 'react';
import { CalendarDays, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { form_error_message } from '@/lib/forms';
import { use_slots, use_reschedule_booking } from '@/features/booking/hooks';
import type { Booking } from '@/features/booking/types';

const IST = 'Asia/Kolkata';
const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
function slot_label(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit' });
}
function current_label(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'medium', timeStyle: 'short' });
}

export function OwnerRescheduleModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const [date, set_date] = useState(today_ist());
  const [slot_start, set_slot_start] = useState<string | null>(null);
  const [error, set_error] = useState<string | null>(null);

  // A vet-confirmed appointment can't be moved unilaterally: the new time goes
  // back to the vet for approval (booking returns to "awaiting vendor").
  const needs_vet_approval = booking.module_type === 'VET' && booking.status === 'CONFIRMED';

  const slots_q = use_slots(booking.service_id, date);
  const reschedule_m = use_reschedule_booking();
  const slots = slots_q.data?.slots ?? [];
  const horizon_days = slots_q.data?.horizon_days;
  const max_bookable_date =
    horizon_days != null
      ? new Date(Date.now() + horizon_days * 86_400_000).toLocaleDateString('en-CA', { timeZone: IST })
      : null;
  const date_beyond_horizon = max_bookable_date != null && date > max_bookable_date;

  async function submit() {
    set_error(null);
    if (!slot_start) {
      set_error('Pick a new time first.');
      return;
    }
    try {
      await reschedule_m.mutateAsync({ id: booking.id, slot_start });
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'That slot is not available. Try another time.'));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={on_close}
    >
      <div
        className="my-12 w-full max-w-md rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="owner-reschedule-modal"
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-heading text-lg font-bold">Reschedule booking</h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">
          <p className="text-sm text-muted-foreground">
            Currently <span className="font-medium text-foreground">{current_label(booking.slot_start)}</span>.{' '}
            {needs_vet_approval
              ? 'This appointment is confirmed by the vet, so the new time is sent as a request — your booking stays pending until the vet approves it.'
              : 'Pick a new open time — the provider is notified.'}
          </p>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reschedule-date">Date</Label>
            <DatePicker
              id="reschedule-date"
              min={today_ist()}
              value={date}
              onChange={(v) => {
                set_date(v);
                set_slot_start(null);
              }}
              className="w-56"
              data-testid="reschedule-date"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Available times</Label>
            {slots_q.isLoading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : slots_q.error ? (
              <p className="text-sm text-destructive" data-testid="reschedule-slots-error">
                {form_error_message(slots_q.error, 'Could not load times.')}
              </p>
            ) : slots.length === 0 ? (
              <div
                className="flex items-center gap-2 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/40 p-3 text-sm text-muted-foreground"
                data-testid="reschedule-slots-empty"
              >
                <CalendarDays className="h-4 w-4" />
                {date_beyond_horizon
                  ? `This provider only takes bookings up to ${horizon_days} days ahead. Pick an earlier date.`
                  : 'No open times on this date. Try another day.'}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="reschedule-slots-grid">
                {slots.map((s) => (
                  <button
                    key={s.slot_start}
                    type="button"
                    onClick={() => set_slot_start(s.slot_start)}
                    data-testid={`reschedule-slot-${s.slot_start}`}
                    className={`rounded-button border-2 px-2 py-2.5 text-sm font-semibold transition-all duration-fast ease-bounce active:scale-95 ${
                      slot_start === s.slot_start
                        ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                        : 'border-input hover:border-primary/50 hover:bg-muted'
                    }`}
                  >
                    {slot_label(s.slot_start)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="reschedule-error">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={on_close}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              onClick={submit}
              disabled={!slot_start || reschedule_m.isPending}
              data-testid="reschedule-submit"
            >
              {reschedule_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{' '}
              {needs_vet_approval ? 'Request reschedule' : 'Reschedule'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
